import { Bar, mixins } from 'vue-chartjs';

// Rounds every bar segment and leaves a small gap between stacked segments, so a stacked
// bar reads as a column of pills. Chart.js 2 has no bar radius option, so each element's
// draw is replaced.
const ROUNDED_BAR_RADIUS = 3;
const ROUNDED_BAR_GAP = 2;
function drawRoundedSegment() {
  const ctx = this._chart.ctx;
  const vm = this._view;
  const half = vm.width / 2;
  const left = vm.x - half;
  const right = vm.x + half;
  let topY = Math.min(vm.y, vm.base);
  let bottom = Math.max(vm.y, vm.base);
  if (bottom - topY > ROUNDED_BAR_GAP * 2) {
    topY += ROUNDED_BAR_GAP / 2;
    bottom -= ROUNDED_BAR_GAP / 2;
  }
  const r = Math.min(ROUNDED_BAR_RADIUS, half, (bottom - topY) / 2);
  ctx.save();
  ctx.beginPath();
  ctx.moveTo(left + r, topY);
  ctx.lineTo(right - r, topY);
  ctx.quadraticCurveTo(right, topY, right, topY + r);
  ctx.lineTo(right, bottom - r);
  ctx.quadraticCurveTo(right, bottom, right - r, bottom);
  ctx.lineTo(left + r, bottom);
  ctx.quadraticCurveTo(left, bottom, left, bottom - r);
  ctx.lineTo(left, topY + r);
  ctx.quadraticCurveTo(left, topY, left + r, topY);
  ctx.closePath();
  ctx.fillStyle = vm.backgroundColor;
  ctx.fill();
  ctx.restore();
}
const roundedBarsPlugin = {
  id: 'otcRoundedBars',
  beforeDatasetsDraw(chart) {
    chart.data.datasets.forEach((dataset, d) => {
      const meta = chart.getDatasetMeta(d);
      if (meta.hidden) return;
      meta.data.forEach((el, b) => {
        const value = Number(dataset.data[b]) || 0;
        el.draw = value > 0 ? drawRoundedSegment : el.constructor.prototype.draw;
      });
    });
  }
};

// HTML tooltip rendered on document.body, so it is never clipped by the canvas. Reuses the
// themed popover panel classes (dark and light mode). The chart's own callbacks (label,
// footer, filter) still shape the content.
function escapeHtml(text) {
  return String(text).replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
}
function renderHtmlTooltip(tooltipModel) {
  const chart = this._chart;
  let el = chart.otcTooltipEl;
  if (!el) {
    el = document.createElement('div');
    el.className = 'el-popover otc-pop otc-pop--panel';
    el.style.position = 'absolute';
    el.style.pointerEvents = 'none';
    el.style.transition = 'opacity 0.1s ease';
    el.style.minWidth = '0';
    el.style.maxHeight = 'none';
    el.style.overflow = 'visible';
    el.style.whiteSpace = 'nowrap';
    el.style.fontSize = '12px';
    el.style.padding = '10px 12px';
    document.body.appendChild(el);
    chart.otcTooltipEl = el;
  }
  if (tooltipModel.opacity === 0) {
    el.style.opacity = 0;
    return;
  }
  // "Label: value" lines split into a left label and a right-aligned value
  const splitLine = (line) => {
    const i = line.lastIndexOf(': ');
    return i < 0 ? [line, ''] : [line.slice(0, i), line.slice(i + 2)];
  };
  const title = (tooltipModel.title || []).map(escapeHtml).join(' ');
  const rows = (tooltipModel.body || []).map((item, i) => {
    const color = tooltipModel.labelColors[i] ? tooltipModel.labelColors[i].backgroundColor : 'transparent';
    const [label, value] = splitLine(item.lines.join(' '));
    return '<div style="display:flex;align-items:center;gap:8px;line-height:1.7">'
      + '<span style="display:inline-block;width:10px;height:10px;border-radius:2px;flex:none;background:' + escapeHtml(color) + '"></span>'
      + '<span style="flex:1">' + escapeHtml(label) + '</span>'
      + '<span style="margin-left:16px;font-variant-numeric:tabular-nums">' + escapeHtml(value) + '</span></div>';
  }).join('');
  const footerLines = (tooltipModel.footer || []).map((line) => {
    const [label, value] = splitLine(line.replace(/^Total /, 'Total: '));
    return '<div style="display:flex;justify-content:space-between;gap:16px;margin-top:6px;padding-top:6px;border-top:1px solid rgba(125,125,125,0.35);font-weight:600">'
      + '<span>' + escapeHtml(label) + '</span><span style="font-variant-numeric:tabular-nums">' + escapeHtml(value) + '</span></div>';
  }).join('');
  el.innerHTML = (title ? '<div style="font-weight:600;margin-bottom:4px">' + title + '</div>' : '') + rows + footerLines;

  const rect = chart.canvas.getBoundingClientRect();
  const anchorX = rect.left + window.pageXOffset + tooltipModel.caretX;
  const anchorY = rect.top + window.pageYOffset + tooltipModel.caretY;
  const width = el.offsetWidth;
  const height = el.offsetHeight;
  const gap = 12;
  // Right of the bar, or left of it when that would run past the viewport
  let left = anchorX + gap;
  if (left + width > window.pageXOffset + document.documentElement.clientWidth - 8) {
    left = anchorX - gap - width;
  }
  let top = anchorY - height / 2;
  const minTop = window.pageYOffset + 8;
  const maxTop = window.pageYOffset + document.documentElement.clientHeight - height - 8;
  top = Math.max(minTop, Math.min(top, maxTop));
  el.style.opacity = 1;
  el.style.left = Math.round(left) + 'px';
  el.style.top = Math.round(top) + 'px';
}

export default {
  name: 'bar-chart',
  extends: Bar,
  mixins: [mixins.reactiveProp],
  props: {
    extraOptions: Object,
    // Rounded segments with a gap between them (opt in; existing dashboards keep square bars)
    rounded: {
      type: Boolean,
      default: false
    },
    // Tooltip as a themed HTML panel outside the canvas, so it is never clipped (opt in)
    htmlTooltip: {
      type: Boolean,
      default: false
    },
    gradientColors: {
      type: Array,
      default: () => [
        'rgba(72,72,176,0.2)',
        'rgba(72,72,176,0.0)',
        'rgba(119,52,169,0)'
      ],
      validator: val => {
        return val.length > 1;
      }
    },
    gradientStops: {
      type: Array,
      default: () => [1, 0.4, 0],
      validator: val => {
        return val.length > 1;
      }
    }
  },
  data() {
    return {
      ctx: null
    };
  },
  methods: {
    updateGradients(chartData) {
      if (!chartData) return;
      const ctx =
        this.ctx || document.getElementById(this.chartId).getContext('2d');
      const gradientStroke = ctx.createLinearGradient(0, 230, 0, 50);

      this.gradientStops.forEach((stop, index) => {
        gradientStroke.addColorStop(stop, this.gradientColors[index]);
      });

      chartData.datasets.forEach(set => {
        if (!set.backgroundColor) {
          set.backgroundColor = gradientStroke;
        }
      });
    }
  },
  beforeDestroy() {
    const chart = this.$data._chart;
    if (chart && chart.otcTooltipEl) {
      chart.otcTooltipEl.remove();
      chart.otcTooltipEl = null;
    }
  },
  mounted() {
    if (this.rounded) {
      this.addPlugin(roundedBarsPlugin);
    }
    this.$watch(
      'chartData',
      (newVal, oldVal) => {
        this.updateGradients(newVal);
        if (!oldVal) {
          let options = this.extraOptions;
          if (this.htmlTooltip) {
            options = { ...options, tooltips: { ...(options && options.tooltips), enabled: false, custom: renderHtmlTooltip } };
          }
          this.renderChart(this.chartData, options);
        }
      },
      { immediate: true }
    );
  }
};
